import { ReactElement, useRef, useState } from 'react';

import { AnimatePresence } from 'framer-motion';

import { BasicFade } from '@components/animators/fade';
import { Slide } from '@components/animators/slide';
import { Button } from '@components/button';
import { useConfirmModal } from '@components/generic-modals/confirm-modal';
import { IconProps } from '@components/icons';
import { IconArrowLeft } from '@components/icons/arrow-left';
import { IconAttributeJourney } from '@components/icons/attribute-journey';
import { IconAttributeTraveller } from '@components/icons/attribute-traveller';
import { IconEdit } from '@components/icons/edit';
import { IconFormIndicatorInfo } from '@components/icons/form-indicator-info';
import { IconReplace } from '@components/icons/replace';
import { InfoBox } from '@components/info-box';
import { RelativeSpinner } from '@components/loading-spinner';
import {
  CloseButton,
  Sidebar,
  forceHideSidebar,
  hideSidebar,
  useSidebar,
} from '@components/sidebar';
import { Stack } from '@components/stack';
import { Text } from '@components/text';
import { useScopedTranslate } from '@locale/translate';
import { XOR } from '@utils/types';

import { useCompanyTravellerAttributeTemplate } from 'api/attributes/hooks';
import {
  AttributeFieldType,
  AttributeTemplateData,
  TravellerAttributeTemplateData,
} from 'api/attributes/requests';
import { isSelectFieldType } from 'utils/attribute-field-types';

import { FileFieldForm } from './field-form-file';
import { NumberFieldForm } from './field-form-number';
import { SelectFieldFlow, SelectFieldFlowRef } from './field-form-select';
import {
  FieldFormData,
  FieldFormSection,
  FieldTypeSelector,
  TypeCard,
  getInputType,
  isFileFieldType,
  isNumberFieldType,
  isSimpleFieldType,
  locale,
} from './field-form-shared';
import { SimpleFieldForm } from './field-form-simple';

// =============================================================================
// Types
// =============================================================================

type AttributeData = XOR<{ attributeId: string }, { attribute: AttributeTemplateData }>;

type AttributeProps<DataType extends AttributeTemplateData | TravellerAttributeTemplateData> = XOR<
  { onCreate: (data: DataType) => Promise<any> },
  { onEdit: (data: DataType) => Promise<any> } & AttributeData
>;

type FieldFormSidebarProps<IsTravellerAttribute extends boolean> = IsTravellerAttribute extends true
  ? {
      isTravellerAttribute: true;
    } & AttributeProps<TravellerAttributeTemplateData> & { shouldAnimate?: boolean }
  : {
      isTravellerAttribute: false;
    } & AttributeProps<TravellerAttributeTemplateData>;

// =============================================================================
// Hook export
// =============================================================================

export const useFieldFormSidebar = () => {
  return useSidebar(FieldFormSidebar, {
    variant: 'CONTENT_HEAVY',
    zIndex: 9992,
    closeOnClickOutside: true,
  });
};

// =============================================================================
// Sidebar router
// =============================================================================

const FieldFormSidebar = <T extends boolean>(props: FieldFormSidebarProps<T>) => {
  if (props.isTravellerAttribute) {
    return <TravellerFieldFormContainer {...props} />;
  }
  return <JourneyFieldForm {...props} />;
};

// =============================================================================
// Sidebar shell — header + animated body
// =============================================================================

type RenderFieldFormParams = {
  chosenType: AttributeFieldType;
  attribute?: TravellerAttributeTemplateData;
  isTravellerAttribute: boolean;
  onSubmit: (data: FieldFormData) => Promise<boolean>;
  isEditing: boolean;
  selectFlowRef?: React.RefObject<SelectFieldFlowRef | null>;
  setCanGoBack?: (canGoBack: boolean) => void;
};

const renderFieldFormByType = ({
  chosenType,
  attribute,
  isTravellerAttribute,
  onSubmit,
  isEditing,
  selectFlowRef,
  setCanGoBack,
}: RenderFieldFormParams): React.ReactNode => {
  const sharedProps = { attribute, isTravellerAttribute, onSubmit, isEditing };

  if (isSimpleFieldType(chosenType)) {
    return <SimpleFieldForm attributeType={chosenType} {...sharedProps} />;
  }
  if (isNumberFieldType(chosenType)) {
    return <NumberFieldForm {...sharedProps} />;
  }
  if (isFileFieldType(chosenType)) {
    return <FileFieldForm {...sharedProps} />;
  }
  if (isSelectFieldType(chosenType)) {
    return (
      <SelectFieldFlow
        attributeType={chosenType}
        {...sharedProps}
        flowRef={selectFlowRef}
        setCanGoBack={setCanGoBack}
      />
    );
  }
  return null;
};

type FieldFormSidebarBaseProps = {
  title: string;
  icon: ReactElement<IconProps>;
  isEditing: boolean;
  shouldAnimate?: boolean;
  initialType?: AttributeFieldType;
  renderForm: (
    chosenType: AttributeFieldType,
    selectFlowRef: React.RefObject<SelectFieldFlowRef | null>,
    setCanGoBack: (canGoBack: boolean) => void
  ) => React.ReactNode;
};

const FieldFormSidebarBase = ({
  title,
  icon,
  isEditing,
  shouldAnimate,
  initialType,
  renderForm,
}: FieldFormSidebarBaseProps) => {
  const [chosenType, setChosenType] = useState<AttributeFieldType | undefined>(initialType);
  const [canGoBack, setCanGoBack] = useState(false);
  const selectFlowRef = useRef<SelectFieldFlowRef | null>(null);

  const handleBack = () => {
    if (chosenType && isSelectFieldType(chosenType) && selectFlowRef.current?.goBack()) {
      return;
    }
    setChosenType(undefined);
  };

  const showBackButton = Boolean(chosenType) && (!isEditing || canGoBack);

  return (
    <Sidebar>
      <FieldFormHeader
        title={title}
        isEditing={isEditing}
        showBackButton={showBackButton}
        onBack={handleBack}
        icon={icon}
      />
      <Sidebar.Content>
        <AnimatePresence initial={shouldAnimate} exitBeforeEnter>
          {chosenType ? (
            <Slide key="field-form" offset={30}>
              {renderForm(chosenType, selectFlowRef, setCanGoBack)}
            </Slide>
          ) : (
            <FieldTypeSelector onSelectType={setChosenType} />
          )}
        </AnimatePresence>
      </Sidebar.Content>
    </Sidebar>
  );
};

// =============================================================================
// Traveller field form
// =============================================================================

const TravellerFieldFormContainer = ({
  attributeId,
  onCreate,
  onEdit,
  shouldAnimate,
}: FieldFormSidebarProps<true>) => {
  const { data, isLoading } = useCompanyTravellerAttributeTemplate(attributeId ?? '', true);

  if (attributeId && (isLoading || !data)) {
    return <RelativeSpinner />;
  }

  const attribute = data
    ? {
        attributeType: data.attributeType,
        title: data.templateTitle ?? '',
        options: data.options,
        isRequired: data.input === 'required',
        isActive: data.input !== 'disabled',
        numTravellers: data.numTravellers,
        isSystemAttribute: data.isSystemAttribute,
      }
    : undefined;

  return (
    <TravellerFieldForm
      attribute={attribute}
      onCreate={onCreate}
      onEdit={onEdit}
      shouldAnimate={shouldAnimate}
    />
  );
};

type TravellerFieldFormInnerProps = {
  attribute?: TravellerAttributeTemplateData;
  onCreate?: (data: TravellerAttributeTemplateData) => Promise<void>;
  onEdit?: (data: TravellerAttributeTemplateData) => Promise<void | undefined>;
  shouldAnimate?: boolean;
};

const TravellerFieldForm = ({
  attribute,
  onCreate,
  onEdit,
  shouldAnimate,
}: TravellerFieldFormInnerProps) => {
  const { t } = useScopedTranslate(locale);
  const showConfirmModal = useConfirmModal();

  const isEditing = Boolean(onEdit);
  const sidebarTitle = onCreate ? t('createTravellerField') : t('editTravellerField');

  const handleSubmit = async (data: FieldFormData) => {
    if (onCreate) {
      await onCreate(data as TravellerAttributeTemplateData);
      forceHideSidebar();
      return true;
    }
    if (onEdit) {
      const numTravellers = attribute?.numTravellers ?? 0;
      if (numTravellers > 0) {
        return new Promise<boolean>((resolve) => {
          showConfirmModal(
            {
              title: t('updateField'),
              description: t('updateFieldDescription', {
                num_travellers: numTravellers,
                travellerNoun: numTravellers === 1 ? t('traveller') : t('travellers'),
              }),
              buttonText: t('updateField'),
              buttonIcon: <IconReplace />,
              onConfirm: async () => {
                await onEdit(data as TravellerAttributeTemplateData);
                forceHideSidebar();
                resolve(true);
              },
            },
            {
              onHide: () => resolve(false),
            }
          );
        });
      }
      await onEdit(data as TravellerAttributeTemplateData);
      forceHideSidebar();
      return true;
    }
    return false;
  };

  const renderForm = (
    chosenType: AttributeFieldType,
    selectFlowRef: React.RefObject<SelectFieldFlowRef | null>,
    setCanGoBack: (canGoBack: boolean) => void
  ) => {
    if (attribute?.isSystemAttribute) {
      return <SystemAttributeView attribute={attribute} attributeType={chosenType} />;
    }
    return renderFieldFormByType({
      chosenType,
      attribute,
      isTravellerAttribute: true,
      onSubmit: handleSubmit,
      isEditing,
      selectFlowRef,
      setCanGoBack,
    });
  };

  return (
    <FieldFormSidebarBase
      title={sidebarTitle}
      icon={<IconAttributeTraveller color="elementBlue100" />}
      isEditing={isEditing}
      shouldAnimate={shouldAnimate}
      initialType={attribute?.attributeType}
      renderForm={renderForm}
    />
  );
};

// =============================================================================
// Journey field form
// =============================================================================

type JourneyFieldFormProps = {
  attribute?: AttributeTemplateData;
  onCreate?: (data: AttributeTemplateData) => Promise<void>;
  onEdit?: (data: AttributeTemplateData) => Promise<void | undefined>;
  shouldAnimate?: boolean;
};

const JourneyFieldForm = ({
  attribute,
  onCreate,
  onEdit,
  shouldAnimate,
}: JourneyFieldFormProps) => {
  const { t } = useScopedTranslate(locale);

  const isEditing = Boolean(onEdit);
  const sidebarTitle = onCreate ? t('createJourneyField') : t('editJourneyField');

  const handleSubmit = async (data: FieldFormData) => {
    if (onCreate) {
      await onCreate(data as AttributeTemplateData);
      forceHideSidebar();
      return true;
    }
    if (onEdit) {
      await onEdit(data as AttributeTemplateData);
      forceHideSidebar();
      return true;
    }
    return false;
  };

  const renderForm = (
    chosenType: AttributeFieldType,
    selectFlowRef: React.RefObject<SelectFieldFlowRef | null>,
    setCanGoBack: (canGoBack: boolean) => void
  ) => {
    return renderFieldFormByType({
      chosenType,
      attribute,
      isTravellerAttribute: false,
      onSubmit: handleSubmit,
      isEditing,
      selectFlowRef,
      setCanGoBack,
    });
  };

  return (
    <FieldFormSidebarBase
      title={sidebarTitle}
      icon={<IconAttributeJourney color="elementBlue100" />}
      isEditing={isEditing}
      shouldAnimate={shouldAnimate}
      initialType={attribute?.attributeType}
      renderForm={renderForm}
    />
  );
};

// =============================================================================
// System attribute view (read-only for traveller system fields)
// =============================================================================

type SystemAttributeViewProps = {
  attribute: TravellerAttributeTemplateData;
  attributeType: AttributeFieldType;
};

const SystemAttributeView = ({ attribute, attributeType }: SystemAttributeViewProps) => {
  const { t } = useScopedTranslate(locale);
  const inputType = getInputType(attribute);

  return (
    <FieldFormSection label={t('details')}>
      <Stack space={6}>
        <Stack space={4}>
          <TypeCard attributeType={attributeType} />
          <div>
            <Text text={t('fieldLabel')} font="semibold14" />
            <Text text={attribute.title} font="regular14" />
          </div>
          <div>
            <Text text={t('input')} font="semibold14" />
            <Text text={t(inputType)} font="regular14" />
          </div>
        </Stack>
        <InfoBox
          icon={<IconFormIndicatorInfo color="indigo100" />}
          text={t('canNotEditSystemField')}
          variant="indigo"
        />
        <Stack orientation="horizontal" justify="flex-end">
          <Button text={t('core.close')} variant="secondary" onClick={hideSidebar} />
        </Stack>
      </Stack>
    </FieldFormSection>
  );
};

// =============================================================================
// Sidebar header
// =============================================================================

type FieldFormHeaderProps = {
  title: string;
  isEditing: boolean;
  showBackButton: boolean;
  onBack: () => void;
  icon: ReactElement<IconProps>;
};

const FieldFormHeader = ({
  title,
  isEditing,
  showBackButton,
  onBack,
  icon,
}: FieldFormHeaderProps) => {
  const { t } = useScopedTranslate(locale);

  return (
    <Sidebar.Header>
      <Stack space={4}>
        <Stack orientation="horizontal" justify="space-between" align="center">
          <Stack space={2} orientation="horizontal" align="center">
            {isEditing ? <IconEdit color="elementBlue100" /> : <div>{icon}</div>}
            <Sidebar.HeaderTitle title={title} />
          </Stack>
          <Stack orientation="horizontal">
            <AnimatePresence initial={false}>
              {showBackButton && (
                <BasicFade>
                  <Button
                    variant="ghost"
                    text={t('core.back')}
                    iconLeft={<IconArrowLeft />}
                    onClick={onBack}
                  />
                </BasicFade>
              )}
            </AnimatePresence>
            <CloseButton />
          </Stack>
        </Stack>
      </Stack>
    </Sidebar.Header>
  );
};
